import type { GameTab } from '../../engine/tabs';
import { showToast } from '../../engine/toast';
import { unitArtFor } from '../../engine/unit-art';
import { buildingArtForLevel, buildingSiteArtFor } from '../../engine/building-art';
import {
  debugAdjustBuildingLevel,
  fetchColony,
  fetchBuildings,
  fetchRoster,
  fetchTech,
  fetchAccommodation,
  fetchConstructionCapacity,
  constructBuilding,
  trainUnit,
  cancelTraining,
  levelUpUnit,
  researchTech,
} from './colony-api';
import { mountColonyCityView, type ColonyCityViewHandle } from './colony-city-view';
import { mountColonyClouds, type ColonyCloudsHandle } from './colony-clouds';
import { mountBoostHud } from '../../engine/boost-hud';
import { icon } from '../../engine/icons';
import { resourceIcon } from '../../engine/resource-icon';
import { attachGameScrollbar, type GameScrollbar } from '../../engine/game-scrollbar';
import { BUILDING_HQ_UNLOCK } from './colony-city-renderer';
// Delta-V is premium currency, so it lives behind the store's balance
// endpoint rather than on ColonyView.resources (which carries only the five
// resources a colony actually produces).
import { fetchStoreBalance } from '../shop/shop-api';
import { t, tOr } from '../../engine/i18n/i18n';
import type {
  ColonyView,
  BuildingView,
  RosterView,
  TechView,
  AccommodationView,
  ConstructionCapacityView,
  ResourceEntry,
  ResourceType,
  BuildingType,
  UnitType,
  UnitCategory,
  ResearchGroup,
} from './colony-api';

/** How often we resync the true server state. Covers network hiccups and
 * server-side changes triggered by other actions (e.g. an attack finishing a
 * construction early). */
const RESYNC_INTERVAL_MS = 30_000;
/** How often we repaint ticking numbers/countdowns between resyncs. Anything
 * shorter is wasted battery — countdowns and amounts only display at
 * second/whole-number granularity. */
const TICK_INTERVAL_MS = 1_000;
/** Minimum time between auto-retries of a stalled countdown-completion
 * refetch, so a server clock skew (countdown says done, server still says
 * constructing) can't turn into a request-per-tick hammering loop. */
const REFETCH_COOLDOWN_MS = 5_000;

const MAX_UNIT_LEVEL = 9;

/** Mirrors backend tech.types.ts's RESEARCH_GROUP_ORDER / RESEARCH_GROUP_STEPS. */
const RESEARCH_GROUP_ORDER: ResearchGroup[] = ['SCOUT', 'BETTER_DEFENCE', 'SIEGE', 'SETTLER'];
const RESEARCH_GROUP_STEPS: Record<ResearchGroup, number> = {
  SCOUT: 2,
  BETTER_DEFENCE: 3,
  SIEGE: 3,
  SETTLER: 2,
};
const researchGroupLabel = (group: ResearchGroup): string => t(`colony.researchGroup.${group}`);
const TOTAL_RESEARCH_STEPS = RESEARCH_GROUP_ORDER.reduce((sum, g) => sum + RESEARCH_GROUP_STEPS[g], 0);

/** Display names come from the shared glossary at call time (never cached), so they follow the active locale. */
const resourceLabel = (type: ResourceType): string => t(`term.resource.${type}`);
const buildingLabel = (type: BuildingType): string => t(`term.building.${type}`);
const unitLabel = (type: UnitType): string => t(`term.unit.${type}`);
const unitRole = (type: UnitType): string => t(`term.unitRole.${type}`);

const BUILDING_ORDER: BuildingType[] = [
  'COMMAND',
  'BUILDER_CAMP',
  'REGOLITH_MINE',
  'WATER_ICE_CONDENSER',
  'HE3_REACTOR',
  'COMPOSITE_FABRICATOR',
  'BARRACKS',
  'SCOUT_GARAGE',
  'BASTION_WALL',
  'SIEGE_WORKSHOP',
  'STRATEGIC_RESERVE',
];

/**
 * Per-building presentation data. `effect` describes what the *next* level
 * buys, using only numbers that actually exist server-side — resource
 * buildings deliberately get a qualitative description because their
 * production rate is not currently derived from building level on the
 * server (see ColonyService's STARTING_RATES_PER_HOUR), so a concrete
 * "+N/hr" figure here would be fiction.
 */
/** Mirrors backend building.types.ts's BUILDING_PRODUCES. */
const BUILDING_PRODUCES: Partial<Record<BuildingType, ResourceType>> = {
  REGOLITH_MINE: 'REGOLITH',
  WATER_ICE_CONDENSER: 'WATER_ICE',
  HE3_REACTOR: 'HE3',
  COMPOSITE_FABRICATOR: 'COMPOSITE',
};

/** Mirrors backend building.types.ts's PRODUCTION_RATE_PER_LEVEL — flat ratePerHour added per level. */
const PRODUCTION_RATE_PER_LEVEL: Partial<Record<BuildingType, number>> = {
  REGOLITH_MINE: 20,
  WATER_ICE_CONDENSER: 10,
  HE3_REACTOR: 8,
  COMPOSITE_FABRICATOR: 5,
};

/**
 * This building's own contribution at the next level — not the colony's
 * live total, which can also be nudged by a founding water-tile bonus or a
 * temporary world event multiplier (see ColonyService.computeAmount).
 */
function productionEffect(type: BuildingType, level: number): string {
  const resourceType = BUILDING_PRODUCES[type]!;
  const perLevel = PRODUCTION_RATE_PER_LEVEL[type]!;
  const next = level + 1;
  return t('colony.effect.production', { resource: resourceLabel(resourceType), amount: perLevel * next, level: next, perLevel });
}

const BUILDING_META: Record<BuildingType, { readonly label: string; readonly blurb: string; effect: (level: number) => string }> = (() => {
  const meta = (
    type: BuildingType,
    effect: (level: number) => string,
  ): { readonly label: string; readonly blurb: string; effect: (level: number) => string } => ({
    // Getters, not values: the label/blurb are looked up per access so a module-level table never freezes a stale locale.
    get label() { return buildingLabel(type); },
    get blurb() { return t(`colony.blurb.${type}`); },
    effect,
  });
  return {
    COMMAND: meta('COMMAND', (level) => t('colony.effect.COMMAND', { level: level + 1 })),
    // Mirrors backend building.types.ts's BUILDER_CAMP_SPEED_BONUS_PER_LEVEL
    // (0.05, same diminishing-returns shape as the Barracks training-speed
    // bonus below) and maxConcurrentConstruction's level-5 threshold.
    BUILDER_CAMP: meta('BUILDER_CAMP', (level) => {
      const next = level + 1;
      const speed = Math.round((1 - 1 / (1 + 0.05 * (next - 1))) * 100);
      return t(next >= 5 ? 'colony.effect.BUILDER_CAMP.raised' : 'colony.effect.BUILDER_CAMP.later', { speed });
    }),
    REGOLITH_MINE: meta('REGOLITH_MINE', (level) => productionEffect('REGOLITH_MINE', level)),
    WATER_ICE_CONDENSER: meta('WATER_ICE_CONDENSER', (level) => productionEffect('WATER_ICE_CONDENSER', level)),
    HE3_REACTOR: meta('HE3_REACTOR', (level) => productionEffect('HE3_REACTOR', level)),
    COMPOSITE_FABRICATOR: meta('COMPOSITE_FABRICATOR', (level) => productionEffect('COMPOSITE_FABRICATOR', level)),
    BARRACKS: meta('BARRACKS', (level) => {
      const next = level + 1;
      const crew = 10 + 5 * next;
      const speed = Math.round((1 - 1 / (1 + 0.05 * (next - 1))) * 100);
      return t(next >= 5 ? 'colony.effect.BARRACKS.slots' : 'colony.effect.BARRACKS.base', { crew, speed });
    }),
    SCOUT_GARAGE: meta('SCOUT_GARAGE', () => t('colony.effect.SCOUT_GARAGE')),
    BASTION_WALL: meta('BASTION_WALL', (level) => t('colony.effect.BASTION_WALL', { power: 15 * (level + 1) })),
    SIEGE_WORKSHOP: meta('SIEGE_WORKSHOP', () => t('colony.effect.SIEGE_WORKSHOP')),
    // Mirrors backend STRATEGIC_RESERVE_STORAGE_BONUS_PER_LEVEL (500) over STARTING_STORAGE_CAP (1000).
    STRATEGIC_RESERVE: meta('STRATEGIC_RESERVE', (level) => t('colony.effect.STRATEGIC_RESERVE', { capacity: 1000 + 500 * (level + 1) })),
  };
})();

const BUILDING_WALL_GATE: Partial<Record<BuildingType, number>> = {
  SIEGE_WORKSHOP: 5,
};

const BUILDING_TECH_GATE: Record<BuildingType, number> = {
  COMMAND: 0,
  REGOLITH_MINE: 0,
  WATER_ICE_CONDENSER: 0,
  HE3_REACTOR: 1,
  COMPOSITE_FABRICATOR: 0,
  BARRACKS: 0,
  SCOUT_GARAGE: 2,
  BASTION_WALL: 0,
  SIEGE_WORKSHOP: 8,
  BUILDER_CAMP: 0,
  STRATEGIC_RESERVE: 1,
};

/** Mirrors backend computeConstructionCost — both scale linearly with the level being reached. */
const REGOLITH_COST_PER_LEVEL = 100;
const WATER_ICE_COST_PER_LEVEL = 50;
const CONSTRUCTION_MINUTES_PER_LEVEL = 5;

/**
 * Mirrors backend MAX_BUILDING_LEVEL. Display-only — the server rejects an
 * over-cap order regardless of what this says.
 */
const MAX_BUILDING_LEVEL = 10;

/**
 * MASTER testing controls — dev build only, and desktop widths only (hidden below `sm`). `import.meta.env.DEV` is Vite's
 * own build-time flag (true under `vite dev`/`yarn dev`, false in any
 * production build), so this block is stripped out of a shipped bundle
 * entirely, not merely hidden by CSS. This is still UI-only: the real gate
 * is the backend's own NODE_ENV check (see BuildingController.debugAdjustLevel) —
 * hiding the buttons here is a courtesy against fat-fingering, not security.
 */
function masterControlsHtml(type: BuildingType, level: number): string {
  if (!import.meta.env.DEV) return '';
  return `
    <div class="hidden sm:flex items-center justify-between gap-2 mb-3 px-2 py-1.5 bg-error/10 border border-error/40 rounded">
      <span class="text-[10px] text-error font-bold uppercase tracking-wide">Master &middot; Lv ${level}</span>
      <div class="flex items-center gap-1.5">
        <button type="button" data-action="debug-adjust-level" data-building-type="${type}" data-delta="-1"
          ${level <= 0 ? 'disabled' : ''}
          class="min-w-11 min-h-11 flex items-center justify-center bg-surface-2 border border-error/50 text-error text-sm font-bold rounded disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation">
          &minus;
        </button>
        <button type="button" data-action="debug-adjust-level" data-building-type="${type}" data-delta="1"
          ${level >= MAX_BUILDING_LEVEL ? 'disabled' : ''}
          class="min-w-11 min-h-11 flex items-center justify-center bg-surface-2 border border-error/50 text-error text-sm font-bold rounded disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation">
          +
        </button>
      </div>
    </div>
  `;
}

/**
 * Mirrors the Command Center's surcharge (backend building.types.ts). HQ is
 * charged in COMPOSITE/HE3 rather than on a steeper REGOLITH curve so no
 * single line item can exceed a colony's base storage cap — see the backend
 * comment for why that matters.
 */
const COMMAND_COMPOSITE_COST_PER_LEVEL = 40;
const COMMAND_HE3_COST_PER_LEVEL = 30;
const COMMAND_DURATION_MULTIPLIER = 4;
/** Mirrors backend COMMAND_COMPOSITE_SURCHARGE_AFTER_LEVEL: Command upgrades to a level above this also cost COMPOSITE. (Not the Fabricator's own unlock level, which is HQ 3.) */
const COMPOSITE_FABRICATOR_HQ_UNLOCK = 5;

function constructionCost(targetLevel: number, type?: BuildingType): Partial<Record<ResourceType, number>> {
  const cost: Partial<Record<ResourceType, number>> = {
    REGOLITH: REGOLITH_COST_PER_LEVEL * targetLevel,
    WATER_ICE: WATER_ICE_COST_PER_LEVEL * targetLevel,
  };
  if (type === 'COMMAND') {
    if (targetLevel > COMPOSITE_FABRICATOR_HQ_UNLOCK) cost.COMPOSITE = COMMAND_COMPOSITE_COST_PER_LEVEL * targetLevel;
    cost.HE3 = COMMAND_HE3_COST_PER_LEVEL * targetLevel;
  }
  return cost;
}

function constructionDurationMs(targetLevel: number, type?: BuildingType): number {
  const minutes = CONSTRUCTION_MINUTES_PER_LEVEL * targetLevel;
  return (type === 'COMMAND' ? minutes * COMMAND_DURATION_MULTIPLIER : minutes) * 60_000;
}

/**
 * Mirrors backend roster.types.ts's BASE_TRAIN_COST_PER_CATEGORY, flattened
 * per unit type (same values, just pre-resolved through UNIT_CATEGORY so
 * this file doesn't need its own copy of that mapping) — display-only, same
 * convention as constructionCost above. The server remains the sole
 * authority on the actual charge; this only lets the player see the cost
 * before committing.
 */
const BASE_TRAIN_COST_PER_UNIT: Record<UnitType, Partial<Record<ResourceType, number>>> = {
  LIGHT_INFANTRY: { REGOLITH: 20, WATER_ICE: 5 },
  DEFENCE_BUNKER: { REGOLITH: 20, WATER_ICE: 5 },
  // BASIC_ATTACK draws on COMPOSITE too (backend BASE_TRAIN_COST_PER_CATEGORY) — this used to omit it, so the popup
  // showed a cost the server then refused to take ("needs composite").
  ATTACK_ROVER: { REGOLITH: 30, WATER_ICE: 10, COMPOSITE: 5 },
  ATTACK_GUNSHIP: { REGOLITH: 30, WATER_ICE: 10, COMPOSITE: 5 },
  SCOUT_BIKE: { REGOLITH: 15, HE3: 5 },
  DEFENCE_TURRET_WALKER: { REGOLITH: 40, COMPOSITE: 10 },
  SIEGE_ARTILLERY: { REGOLITH: 80, COMPOSITE: 20, HE3: 15 },
  SIEGE_MORTAR_TANK: { REGOLITH: 80, COMPOSITE: 20, HE3: 15 },
  SETTLER_CONVOY: { REGOLITH: 400, WATER_ICE: 200, COMPOSITE: 150, HE3: 100, CRIMSON_DUST: 50 },
};

function trainingCost(unitType: UnitType, quantity: number): Partial<Record<ResourceType, number>> {
  const base = BASE_TRAIN_COST_PER_UNIT[unitType];
  const cost: Partial<Record<ResourceType, number>> = {};
  for (const [res, amt] of Object.entries(base) as [ResourceType, number][]) {
    cost[res] = amt * quantity;
  }
  return cost;
}

function trainingCostHtml(unitType: UnitType, quantity: number): string {
  const cost = trainingCost(unitType, quantity);
  return (Object.entries(cost) as [ResourceType, number][])
    .map(([res, amt]) => `<span class="inline-flex items-center gap-1">${resourceIcon(res)}${resourceLabel(res)} ${formatAmount(amt)}</span>`)
    .join('<span class="text-text-dim mx-1">&middot;</span>');
}

const UNIT_ORDER: UnitType[] = [
  'LIGHT_INFANTRY',
  'DEFENCE_BUNKER',
  'ATTACK_ROVER',
  'ATTACK_GUNSHIP',
  'SCOUT_BIKE',
  'DEFENCE_TURRET_WALKER',
  'SIEGE_ARTILLERY',
  'SIEGE_MORTAR_TANK',
  'SETTLER_CONVOY',
];

/**
 * Mirrors backend battle.formulas.ts's UNIT_BASE_POWER (attack) + UNIT_BASE_DEFENCE + DEFENDING_UNIT_TYPES,
 * battle.constants.ts's UNIT_MARCH_MINUTES_PER_TILE, and battle.formulas.ts's
 * UNIT_CARRY_CAPACITY — display-only, same "duplicate constants, no shared
 * package" convention as BASE_TRAIN_COST_PER_UNIT above. A unit has TWO powers:
 * Attack (when it is sent on a march) and Defence (when it stands in its home
 * garrison). Only the DEFENDING_UNIT_TYPES subset auto-garrisons — any other
 * unit contributes nothing to a standing defense no matter how many are held,
 * so its Defence reads "—". Light Infantry is balanced (8 / 8); the Sector
 * Holder (DEFENCE_BUNKER) trades attack for defence (4 / 16).
 */
const DEFENDING_UNIT_TYPES: UnitType[] = ['LIGHT_INFANTRY', 'DEFENCE_BUNKER', 'DEFENCE_TURRET_WALKER'];

const UNIT_STATS: Record<UnitType, { power: number; defence?: number; minutesPerTile: number; carryCapacity: number }> = {
  LIGHT_INFANTRY: { power: 8, defence: 8, minutesPerTile: 5, carryCapacity: 25 },
  DEFENCE_BUNKER: { power: 4, defence: 16, minutesPerTile: 5, carryCapacity: 10 },
  ATTACK_ROVER: { power: 10, minutesPerTile: 5, carryCapacity: 80 },
  ATTACK_GUNSHIP: { power: 16, minutesPerTile: 2, carryCapacity: 50 },
  SCOUT_BIKE: { power: 0, minutesPerTile: 1.5, carryCapacity: 0 },
  DEFENCE_TURRET_WALKER: { power: 26, defence: 26, minutesPerTile: 5, carryCapacity: 40 },
  SIEGE_ARTILLERY: { power: 22, minutesPerTile: 12, carryCapacity: 0 },
  SIEGE_MORTAR_TANK: { power: 30, minutesPerTile: 12, carryCapacity: 0 },
  SETTLER_CONVOY: { power: 2, minutesPerTile: 10, carryCapacity: 0 },
};

function unitStatsHtml(unitType: UnitType): string {
  const stats = UNIT_STATS[unitType];
  const isDefending = DEFENDING_UNIT_TYPES.includes(unitType);
  const stat = (label: string, value: string): string =>
    `<span class="inline-flex items-center gap-1"><span class="text-text-dim">${label}</span><span class="tabular-nums text-text">${value}</span></span>`;
  return `<div class="flex flex-wrap gap-x-2.5 gap-y-0.5 text-[10px] mb-1.5">${[
    stat(t('colony.stat.atk'), String(stats.power)),
    stat(t('colony.stat.def'), isDefending && stats.defence !== undefined ? String(stats.defence) : '—'),
    stat(t('colony.stat.spd'), t('colony.stat.speedValue', { minutes: stats.minutesPerTile })),
    stat(t('colony.stat.carry'), stats.carryCapacity > 0 ? String(stats.carryCapacity) : '—'),
  ].join('')}</div>`;
}

/** Mirrors backend UNIT_TRAINING_BUILDING — which building must exist (level >= 1) before a unit can be trained. */
const UNIT_TRAINING_BUILDING: Record<UnitType, BuildingType> = {
  LIGHT_INFANTRY: 'BARRACKS',
  DEFENCE_BUNKER: 'BARRACKS',
  ATTACK_ROVER: 'BARRACKS',
  ATTACK_GUNSHIP: 'BARRACKS',
  SCOUT_BIKE: 'SCOUT_GARAGE',
  DEFENCE_TURRET_WALKER: 'BARRACKS',
  SIEGE_ARTILLERY: 'SIEGE_WORKSHOP',
  SIEGE_MORTAR_TANK: 'SIEGE_WORKSHOP',
  SETTLER_CONVOY: 'BARRACKS',
};

/** Mirrors backend roster.types.ts's UNIT_UNLOCK_BUILDING_LEVEL. Absent = no gate beyond level 1. */
const UNIT_UNLOCK_BUILDING_LEVEL: Partial<Record<UnitType, number>> = {
  ATTACK_ROVER: 3,
  ATTACK_GUNSHIP: 6,
};

/**
 * Mirrors backend tech.types.ts's UNIT_UNLOCK_RESEARCH_GROUP — an extra gate
 * on top of a unit's own (always-unlocked) category. Keyed by category rather
 * than group: `tech.unlockedCategories` already encodes exactly "every group
 * up to and including this one is fully completed", since GROUP_UNLOCKS_CATEGORY
 * maps each research group onto the identically-named category.
 */
const UNIT_UNLOCK_RESEARCH_CATEGORY: Partial<Record<UnitType, UnitCategory>> = {
  ATTACK_GUNSHIP: 'BETTER_DEFENCE',
};

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

function formatAmount(value: number): string {
  return Math.floor(value).toLocaleString('en-US');
}

function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

function formatDuration(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return t('colony.duration.minutes', { minutes });
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem === 0 ? t('colony.duration.hours', { hours }) : t('colony.duration.hoursMinutes', { hours, minutes: rem });
}

/** Mirrors ColonyService.computeAmount server-side so displayed numbers tick
 * up smoothly between resyncs without drifting from the authoritative value. */
function computeDisplayedAmount(resource: ResourceEntry, fetchedAt: number): number {
  const elapsedHours = (Date.now() - fetchedAt) / 3_600_000;
  return Math.min(resource.storageCap, resource.amount + resource.ratePerHour * elapsedHours);
}

function mergeBuildings(fetched: BuildingView[]): BuildingView[] {
  const byType = new Map(fetched.map((b) => [b.type, b]));
  return BUILDING_ORDER.map(
    (type) => byType.get(type) ?? { type, level: 0, constructing: false, constructionEndAt: null },
  );
}

function mergeRoster(fetched: RosterView[]): RosterView[] {
  const byType = new Map(fetched.map((u) => [u.unitType, u]));
  return UNIT_ORDER.map(
    (unitType) =>
      byType.get(unitType) ?? {
        unitType,
        level: 0,
        quantityReserved: 0,
        training: false,
        trainEndAt: null,
        trainQuantity: 0,
        nextUnitAt: null,
        locked: true,
        crewSize: 0,
      },
  );
}

export function renderColonyScreen(root: HTMLElement): () => void {
  // Full-bleed like the world map: the colony view is the primary surface,
  // not a card floating in a padded column.
  root.innerHTML = `
    <div id="colony-screen" class="w-full h-full flex flex-col">
      <div class="text-center text-text-dim py-10">${t('colony.loading')}</div>
    </div>
  `;
  const container = root.querySelector('#colony-screen') as HTMLDivElement;

  let disposed = false;
  let loaded = false;

  let colony: ColonyView | null = null;
  let colonyFetchedAt = 0;
  let buildings: BuildingView[] = [];
  let roster: RosterView[] = [];
  let tech: TechView | null = null;
  let accommodation: AccommodationView | null = null;
  /** Premium-currency balance. Null until the first /store/balance call lands — the row is simply omitted rather than showing a misleading 0. */
  let deltaV: number | null = null;
  let capacity: ConstructionCapacityView | null = null;

  let buildingsInFlight = false;
  let buildingsRetryAfter = 0;
  let rosterInFlight = false;
  let rosterRetryAfter = 0;
  let techInFlight = false;
  let techRetryAfter = 0;

  let cityView: ColonyCityViewHandle | null = null;
  let clouds: ColonyCloudsHandle | null = null;
  let boostHudTeardown: (() => void) | null = null;

  /**
   * The modal lives on `root`, deliberately OUTSIDE `container` — render()
   * replaces container.innerHTML on every data refresh, which would destroy
   * an open modal mid-interaction (construct -> refetch -> render -> gone).
   * Same survival strategy the city view canvas uses.
   */
  let openBuildingType: BuildingType | null = null;
  let modalEl: HTMLDivElement | null = null;

  /** Phones: Resources / Power / Units each open as their own popup instead of a scrolling column. */
  type DataKind = 'resources' | 'power' | 'units';
  let dataKind: DataKind | null = null;
  let dataEl: HTMLDivElement | null = null;

  function buildingOf(type: BuildingType): BuildingView {
    return buildings.find((b) => b.type === type) ?? { type, level: 0, constructing: false, constructionEndAt: null };
  }

  /**
   * A unit is trainable only if its tech category is unlocked (`unit.locked`,
   * computed server-side), its training building meets
   * UNIT_UNLOCK_BUILDING_LEVEL (default: exists at all, level >= 1), and — for
   * the few units with an extra gate on top of their own category —
   * UNIT_UNLOCK_RESEARCH_CATEGORY's group is also fully completed.
   */
  function isTrainable(unit: RosterView): boolean {
    if (unit.locked) return false;
    const requiredBuildingLevel = UNIT_UNLOCK_BUILDING_LEVEL[unit.unitType] ?? 1;
    if (buildingOf(UNIT_TRAINING_BUILDING[unit.unitType]).level < requiredBuildingLevel) return false;
    const requiredCategory = UNIT_UNLOCK_RESEARCH_CATEGORY[unit.unitType];
    if (requiredCategory !== undefined && !(tech?.unlockedCategories.includes(requiredCategory) ?? false)) return false;
    return true;
  }

  function trainableUnits(): RosterView[] {
    return roster.filter(isTrainable);
  }

  // ---------------------------------------------------------------- sidebar

  function resourceRowHtml(resource: ResourceEntry): string {
    const amount = computeDisplayedAmount(resource, colonyFetchedAt);
    return `
      <div class="row-glare flex items-center gap-2 py-1.5 border-b border-border/50 last:border-b-0">
        <span class="text-xs text-text-dim flex-1 min-w-0 truncate">${resourceLabel(resource.type)}</span>
        <div class="text-right shrink-0">
          <div class="font-heading text-sm text-text tabular-nums" data-resource-amount="${resource.type}">${formatAmount(amount)}</div>
          <div class="text-[10px] text-text-dim tabular-nums" data-resource-meta="${resource.type}">${t('colony.ratePerHour', { rate: formatAmount(resource.ratePerHour) })}</div>
        </div>
      </div>
    `;
  }

  /**
   * BUILDING_META.effect() returns prose, and several entries pack more than
   * one fact into it joined by "·" (Barracks: crew capacity, training speed,
   * slot count). Split on that existing separator so each fact gets its own
   * line in the value column instead of one long sentence that wraps and
   * breaks the grid's alignment. Single-fact effects are unaffected — they
   * simply yield one line.
   */
  function effectLinesHtml(effect: string): string {
    return effect
      .split('·')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => `<span>${escapeHtml(part)}</span>`)
      .join('');
  }

  /**
   * Delta-V sits in the Resources panel alongside the five mined/condensed
   * resources, but it is deliberately set apart: it is the only one a colony
   * cannot produce. The "Shipments from outside" sub-label says exactly that,
   * standing where every other row shows a +N/hr production rate — a colony
   * has no Delta-V rate, because none of it comes from Mars.
   *
   * Omitted entirely until /store/balance has answered once, rather than
   * rendering a placeholder 0 that would read as "you are broke".
   */
  function deltaVRowHtml(): string {
    if (deltaV === null) return '';
    return `
      <div class="deltav-row row-glare flex items-center gap-2 py-1.5 px-2 mt-1 -mx-1.5 rounded">
        <span class="text-xs font-bold flex-1 min-w-0 truncate">${t('term.deltaV')}</span>
        <div class="text-right shrink-0">
          <div class="font-heading text-sm font-bold tabular-nums" data-delta-v-amount>${formatAmount(deltaV)}</div>
          <div class="text-[10px] font-bold opacity-80">${t('colony.deltaVShipments')}</div>
        </div>
      </div>
    `;
  }

  /**
   * "Power" in this colony is crew accommodation — the Barracks houses the
   * crews that man units. Labelled explicitly as Barracks crew because the
   * server only counts Barracks-trained types; scout and siege units are not
   * crew-gated, so this is not a global unit cap.
   */
  function powerSectionHtml(): string {
    if (!accommodation) {
      return `<p class="text-xs text-text-dim">${t('colony.crewUnavailable')}</p>`;
    }
    const { crewUsed, crewCapacity, concurrentTrainingSlotsUsed, concurrentTrainingSlotsMax } = accommodation;
    const free = Math.max(0, crewCapacity - crewUsed);
    const pct = crewCapacity > 0 ? Math.min(100, (crewUsed / crewCapacity) * 100) : 0;
    const bar = free === 0 && crewCapacity > 0 ? 'bg-crimson' : 'bg-gold';
    return `
      <div class="row-glare flex items-baseline justify-between gap-2 mb-1">
        <span class="text-xs text-text-dim">${t('colony.barracksCrew')}</span>
        <span class="font-heading text-sm text-text tabular-nums" data-crew-usage>${crewUsed} / ${crewCapacity}</span>
      </div>
      <div class="h-1.5 w-full bg-surface-2 rounded overflow-hidden mb-1">
        <div class="h-full ${bar} transition-all duration-[200ms]" style="width:${pct}%"></div>
      </div>
      <div class="row-glare flex items-baseline justify-between gap-2">
        <span class="text-[10px] text-text-dim" data-crew-free>${t('colony.free', { free })}</span>
        <span class="text-[10px] text-text-dim">${t('colony.trainingSlots', { used: concurrentTrainingSlotsUsed, max: concurrentTrainingSlotsMax })}</span>
      </div>
    `;
  }

  /** Same crew data as powerSectionHtml's sidebar summary, repeated inline inside the Barracks modal itself — a player deciding how many units to train shouldn't have to glance away to see how much room is left. */
  function barracksCrewSummaryHtml(): string {
    if (!accommodation) return '';
    const { crewUsed, crewCapacity } = accommodation;
    const free = Math.max(0, crewCapacity - crewUsed);
    return `
      <div class="flex items-baseline justify-between gap-2 mb-2 text-xs">
        <span class="text-text-dim">${t('colony.crewSpace')}</span>
        <span class="text-text tabular-nums" data-modal-crew-usage>${crewUsed} / ${crewCapacity} <span class="text-text-dim">(${t('colony.free', { free })})</span></span>
      </div>
    `;
  }

  function unitSummaryHtml(): string {
    const available = trainableUnits();
    if (available.length === 0) {
      return `<p class="text-xs text-text-dim leading-relaxed">${t('colony.noUnits', { barracks: `<span class="text-gold">${escapeHtml(buildingLabel('BARRACKS'))}</span>` })}</p>`;
    }
    return available
      .map((unit) => {
        const training = unit.training && unit.nextUnitAt
          ? `<span class="text-[10px] text-gold-bright tabular-nums" data-roster-countdown="${unit.unitType}">+${unit.trainQuantity} · ${formatCountdown(new Date(unit.nextUnitAt).getTime() - Date.now())}</span>`
          : '';
        return `
          <div class="row-glare flex items-center gap-2 py-1.5 border-b border-border/50 last:border-b-0">
            <div class="flex-1 min-w-0">
              <div class="text-xs text-text truncate">${unitLabel(unit.unitType)}</div>
              ${training}
            </div>
            <span class="font-heading text-sm text-text tabular-nums shrink-0" data-unit-qty="${unit.unitType}">${unit.quantityReserved}</span>
          </div>
        `;
      })
      .join('');
  }

  function sidebarHtml(): string {
    if (!colony) return '';
    return `
      <section>
        <h2 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">${t('colony.sidebar.resources')}</h2>
        <div class="glass-panel px-3 py-1">
          ${colony.resources.map(resourceRowHtml).join('')}
          ${deltaVRowHtml()}
        </div>
      </section>
      <section>
        <h2 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">${t('colony.sidebar.power')}</h2>
        <div class="glass-panel px-3 py-2" data-power-section>
          ${powerSectionHtml()}
        </div>
      </section>
      <section>
        <h2 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">${t('colony.sidebar.units')}</h2>
        <div class="glass-panel px-3 py-1" data-units-section>
          ${unitSummaryHtml()}
        </div>
      </section>
    `;
  }

  /** One of the big square phone buttons (see `.colony-data-btn`). */
  function dataButton(kind: DataKind, iconName: 'crate' | 'shield' | 'troops', label: string): string {
    return `<button type="button" data-action="open-data" data-data-kind="${kind}" aria-label="${escapeHtml(label)}"
      class="colony-data-btn touch-manipulation"><span class="colony-data-btn-ico" aria-hidden="true">${icon(iconName)}</span><span class="colony-data-btn-label">${escapeHtml(label)}</span></button>`;
  }

  function dataPopupBodyHtml(kind: DataKind): string {
    if (!colony) return '';
    if (kind === 'resources') {
      return `<div class="glass-panel px-3 py-1">${colony.resources.map(resourceRowHtml).join('')}${deltaVRowHtml()}</div>`;
    }
    if (kind === 'power') return `<div class="glass-panel px-3 py-2">${powerSectionHtml()}</div>`;
    return `<div class="glass-panel px-3 py-1">${unitSummaryHtml()}</div>`;
  }

  const DATA_TITLE = {
    resources: 'colony.sidebar.resources',
    power: 'colony.sidebar.power',
    units: 'colony.sidebar.units',
  } as const;

  /** Re-renders the open popup's table in place (called from render() and every tick, so figures stay live). */
  function refreshDataPopup(): void {
    if (!dataEl || !dataKind) return;
    const body = dataEl.querySelector('[data-data-body]');
    if (body) body.innerHTML = dataPopupBodyHtml(dataKind);
  }

  let dataScrollbar: GameScrollbar | null = null;
  let modalScrollbar: GameScrollbar | null = null;

  function closeDataPopup(): void {
    dataScrollbar?.destroy();
    dataScrollbar = null;
    dataEl?.remove();
    dataEl = null;
    dataKind = null;
  }

  function openDataPopup(kind: DataKind): void {
    closeDataPopup();
    dataKind = kind;
    const el = document.createElement('div');
    el.className = 'fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', t(DATA_TITLE[kind]));
    el.innerHTML = `
      <div data-modal-panel
        class="popup-enter instrument-wash w-full sm:max-w-md max-h-[80dvh] flex flex-col overflow-hidden bg-surface border border-border border-t-2 border-t-gold rounded-t-xl sm:rounded-xl panel-glow pt-3 px-3">
        <div class="flex items-center justify-between gap-3 mb-2 shrink-0">
          <h2 class="font-heading text-gold text-base">${t(DATA_TITLE[kind])}</h2>
          <button type="button" data-action="close-data" aria-label="${t('common.close')}"
            class="min-w-11 min-h-11 -m-2 flex items-center justify-center text-text-dim hover:text-text leading-none text-xl shrink-0">&times;</button>
        </div>
        <div data-data-body class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain pb-[calc(0.75rem+env(safe-area-inset-bottom))]" style="-webkit-overflow-scrolling: touch">${dataPopupBodyHtml(kind)}</div>
      </div>
    `;
    el.addEventListener('click', (e) => {
      if (e.target === el) closeDataPopup();
    });
    root.appendChild(el);
    dataEl = el;
    dataScrollbar = attachGameScrollbar(el.querySelector('[data-data-body]') as HTMLElement);
  }

  function render(): void {
    if (!colony || !tech) return;
    // Phones get an icon + "used/max" (hammer, flask); sm and up keep the written labels.
    const slotsText = capacity ? t('colony.buildingSlots', { used: capacity.concurrentConstructionUsed, max: capacity.concurrentConstructionMax }) : '';
    const techText = t('colony.techSummary', { done: tech.totalStepsCompleted, total: TOTAL_RESEARCH_STEPS });
    const compact = (name: 'hammer' | 'research', value: string, label: string): string =>
      `<span class="sm:hidden inline-flex items-center gap-1 text-text-dim text-xs tabular-nums" title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}"><span class="w-4 h-4 inline-block" aria-hidden="true">${icon(name)}</span>${value}</span>`;
    const constructionLine = capacity
      ? `${compact('hammer', `${capacity.concurrentConstructionUsed}/${capacity.concurrentConstructionMax}`, slotsText)}<span class="hidden sm:inline text-text-dim text-xs">${slotsText}</span>`
      : '';
    const techLine = `${compact('research', `${tech.totalStepsCompleted}/${TOTAL_RESEARCH_STEPS}`, techText)}<span class="hidden sm:inline text-text-dim text-xs">${techText}</span>`;
    container.innerHTML = `
      <!-- Phones: the name lives in its own plate over the colony view (below); this bar holds the tier on the left
           and the build/research counters on the right. sm and up: name + tier on the left, counters on the right. -->
      <div class="mars-horizon flex items-center justify-between gap-3 px-3 py-2 border-b border-border shrink-0">
        <!-- Name + tier travel together (so they can never overlap) and are the bar's first child, which the map/colony
             header styling turns into a left patch. On phones the name moves to its own plate over the view, leaving
             just the tier here. NOTE: never add the flex-wrap class to this bar — that class is how the header styling
             recognises (and skips) the map's wrapping title strip, and it would strip this bar's patches. -->
        <div class="flex items-baseline gap-2 min-w-0">
          <h1 class="hidden sm:block min-w-0 font-heading text-base text-gold tracking-wide truncate [text-shadow:0_1px_8px_rgba(0,0,0,0.7)]" data-colony-name>${escapeHtml(colony.name)}</h1>
          <span class="tier-badge shrink-0" data-colony-tier>${t('colony.tier', { tier: colony.tier })}</span>
        </div>
        <div class="flex items-center gap-3 shrink-0">
          ${constructionLine}
          ${techLine}
        </div>
      </div>

      <div class="flex-1 min-h-0 flex flex-col lg:flex-row">
        <div data-city-view-host
          class="relative flex-1 min-h-0 lg:flex-none lg:w-[calc(100%-16rem)] bg-surface-2 border-b border-border lg:border-b-0 lg:border-r overflow-hidden">
          <!-- Phones only: colony name in its own plate, centred over the top of the view. -->
          <h1 class="colony-name-plate sm:hidden absolute z-10 font-heading text-sm text-gold tracking-wide truncate" data-colony-name>${escapeHtml(colony.name)}</h1>
          <!-- Phones only: the data tables as big square buttons down the right edge, each opening its own popup. -->
          <div class="colony-data-btns lg:hidden absolute z-10 flex flex-col gap-2">
            ${dataButton('resources', 'crate', t('colony.sidebar.resources'))}
            ${dataButton('power', 'shield', t('colony.sidebar.power'))}
            ${dataButton('units', 'troops', t('colony.sidebar.units'))}
          </div>
          <!-- Running boosts, overlaid on the side of the city view (the canvas is a sibling the view re-parents in). -->
          <div data-boost-hud class="colony-boost-hud absolute z-10 flex-col items-start gap-1.5" hidden></div>
        </div>
        <aside data-colony-data class="max-lg:hidden colony-data-fade instrument-wash flex-1 min-h-0 overflow-y-auto overscroll-contain lg:flex-none lg:w-[20.8rem] lg:-ml-[4.8rem] lg:pl-4 lg:relative lg:z-10 p-3 flex flex-col gap-3">
          ${sidebarHtml()}
        </aside>
      </div>
    `;
    boostHudTeardown?.();
    const hudHost = container.querySelector('[data-boost-hud]') as HTMLElement | null;
    boostHudTeardown = hudHost ? mountBoostHud(hudHost) : null;
    cityView?.redraw();
    refreshDataPopup();
  }

  /** Patches only the numbers that tick between full re-renders, so an
   * in-progress quantity input (or scroll position) isn't disturbed. */
  function patchResources(): void {
    if (!colony) return;
    container.querySelectorAll('[data-colony-name]').forEach((el) => { el.textContent = colony!.name; });
    const tierEl = container.querySelector('[data-colony-tier]');
    if (tierEl) tierEl.textContent = t('colony.tier', { tier: colony.tier });
    for (const resource of colony.resources) {
      const amountEl = container.querySelector(`[data-resource-amount="${resource.type}"]`);
      if (amountEl) amountEl.textContent = formatAmount(computeDisplayedAmount(resource, colonyFetchedAt));
      const metaEl = container.querySelector(`[data-resource-meta="${resource.type}"]`);
      if (metaEl) metaEl.textContent = t('colony.ratePerHour', { rate: formatAmount(resource.ratePerHour) });
    }
  }

  // ------------------------------------------------------------------ modal

  function closeModal(): void {
    modalScrollbar?.destroy();
    modalScrollbar = null;
    modalEl?.remove();
    modalEl = null;
    openBuildingType = null;
  }

  function trainingRowsHtml(type: BuildingType): string {
    const units = trainableUnits().filter((u) => UNIT_TRAINING_BUILDING[u.unitType] === type);
    if (units.length === 0) {
      return `<p class="text-xs text-text-dim">${t('colony.noUnitTypes')}</p>`;
    }
    return units
      .map((unit) => {
        const label = unitLabel(unit.unitType);
        let action: string;
        if (unit.training && unit.nextUnitAt) {
          const remaining = new Date(unit.nextUnitAt).getTime() - Date.now();
          action = `
            <div class="flex items-center gap-2 flex-wrap">
              <div class="text-xs text-gold-bright tabular-nums" data-modal-roster-countdown="${unit.unitType}">${t('colony.trainingCountdown', { quantity: unit.trainQuantity, time: formatCountdown(remaining) })}</div>
              <button type="button" data-action="cancel-train" data-unit-type="${unit.unitType}"
                class="min-h-11 px-3 bg-surface-2 border border-error/50 text-error text-xs font-bold rounded transition-colors duration-[120ms] hover:bg-error/10 touch-manipulation">
                ${t('common.cancel')}
              </button>
            </div>
          `;
        } else {
          action = `
            <div class="flex items-center gap-2 flex-wrap">
              <input type="number" min="1" step="1" value="1" inputmode="numeric" aria-label="${escapeHtml(t('colony.quantityLabel', { unit: label }))}"
                data-quantity-input="${unit.unitType}"
                class="w-16 min-h-11 px-2 bg-surface-2 border border-border rounded text-text text-sm tabular-nums touch-manipulation" />
              <button type="button" data-action="train-unit" data-unit-type="${unit.unitType}"
                class="min-h-11 px-3 bg-gold text-bg text-sm font-bold rounded transition-colors duration-[120ms] hover:bg-gold-bright touch-manipulation">
                ${t('colony.train')}
              </button>
              <button type="button" data-action="level-up-unit" data-unit-type="${unit.unitType}"
                ${unit.level >= MAX_UNIT_LEVEL || (unit.quantityReserved === 0 && unit.trainQuantity === 0) ? 'disabled' : ''}
                title="${unit.quantityReserved === 0 && unit.trainQuantity === 0 ? t('colony.levelUpHint') : ''}"
                class="min-h-11 px-3 bg-purple/20 border border-purple text-purple-light text-sm font-bold rounded transition-colors duration-[120ms] hover:bg-purple/30 disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation">
                ${t('colony.levelUp')}
              </button>
            </div>
            <div class="text-[10px] text-text-dim mt-1.5 tabular-nums" data-training-cost="${unit.unitType}">${t('colony.cost', { cost: trainingCostHtml(unit.unitType, 1) })}</div>
          `;
        }
        const art = unitArtFor(unit.unitType);
        return `
          <div class="py-2 border-b border-border/50 last:border-b-0 flex gap-3">
            ${art
              ? `<img src="${art.thumb}" alt="" aria-hidden="true" loading="lazy"
                   title="${tOr(`term.unitClass.${unit.unitType}`, art.className)}"
                   class="w-14 h-14 shrink-0 object-contain self-start" />`
              : ''}
            <div class="min-w-0 flex-1">
              <div class="flex items-baseline justify-between gap-2 mb-1.5">
                <span class="text-sm text-text">${label}</span>
                <span class="text-[10px] text-text-dim">${unit.crewSize > 0 ? t('colony.unitMetaCrew', { level: unit.level, held: unit.quantityReserved, crew: unit.crewSize }) : t('colony.unitMeta', { level: unit.level, held: unit.quantityReserved })}</span>
              </div>
              <div class="text-[10px] text-text-dim mb-1.5">${unitRole(unit.unitType)}</div>
              ${unitStatsHtml(unit.unitType)}
              ${action}
            </div>
          </div>
        `;
      })
      .join('');
  }

  function techBlockHtml(): string {
    if (!tech) return '';
    let control: string;
    let progress = '';
    if (tech.researching && tech.researchEndAt) {
      const remaining = new Date(tech.researchEndAt).getTime() - Date.now();
      control = `<span class="text-sm text-gold-bright tabular-nums" data-tech-countdown>${formatCountdown(remaining)}</span>`;
      // Research steps run 10 minutes to 1.5 hours, so a bare MM:SS countdown
      // gave no sense of scale. `researchStartAt` (newly surfaced by the API)
      // is what makes elapsed-of-total computable at all — without it the
      // client knows when research ends but not how long it was.
      if (tech.researchStartAt) {
        const start = new Date(tech.researchStartAt).getTime();
        const total = new Date(tech.researchEndAt).getTime() - start;
        const done = Math.min(total, Math.max(0, Date.now() - start));
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        progress = `
          <div class="mb-2" data-tech-progress>
            <div class="h-1 rounded-full bg-surface-2 overflow-hidden">
              <div class="h-full bg-gold-bright" style="width:${pct}%"></div>
            </div>
            <div class="flex justify-between text-[10px] text-text-dim mt-1 tabular-nums">
              <span>${t('colony.research.elapsed', { time: formatDuration(done) })}</span>
              <span>${t('colony.research.total', { time: formatDuration(total) })}</span>
            </div>
          </div>
        `;
      }
    } else {
      const disabled = tech.currentGroup === null;
      control = `
        <button type="button" data-action="research-tech" ${disabled ? 'disabled' : ''}
          class="font-button min-h-11 px-4 bg-gold text-bg text-sm rounded transition-colors duration-[120ms] hover:bg-gold-bright disabled:bg-border disabled:text-text-dim disabled:cursor-not-allowed touch-manipulation">
          ${t('colony.research.button')}
        </button>
      `;
    }
    return `
      <div class="mt-3 pt-3 border-t border-border">
        <div class="flex items-center justify-between gap-3 flex-wrap mb-2">
          <span class="text-sm text-text">${t('colony.research.progress', { done: `<span class="text-gold-bright font-bold">${tech.totalStepsCompleted}</span>`, total: TOTAL_RESEARCH_STEPS })}</span>
          ${control}
        </div>
        ${progress}
        <div class="flex flex-col gap-1.5">
          ${RESEARCH_GROUP_ORDER.map((group) => researchGroupRowHtml(group)).join('')}
        </div>
      </div>
    `;
  }

  /** One row per research group: a label plus one bar-segment per step, reusing the pill chip-on/chip-off/chip-researching treatments per segment. */
  function researchGroupRowHtml(group: ResearchGroup): string {
    const steps = RESEARCH_GROUP_STEPS[group];
    const groupIdx = RESEARCH_GROUP_ORDER.indexOf(group);
    const currentIdx = tech!.currentGroup === null ? RESEARCH_GROUP_ORDER.length : RESEARCH_GROUP_ORDER.indexOf(tech!.currentGroup);
    const segments = Array.from({ length: steps }, (_, stepIndex) => {
      if (groupIdx < currentIdx) return 'chip-on';
      if (groupIdx > currentIdx) return 'chip-off';
      if (stepIndex < tech!.currentStep) return 'chip-on';
      if (stepIndex === tech!.currentStep && tech!.researching) return 'chip-researching';
      return 'chip-off';
    });
    return `
      <div class="flex items-center gap-2" data-research-group="${group}">
        <span class="text-[11px] text-text-dim w-24 shrink-0 truncate">${researchGroupLabel(group)}</span>
        <div class="flex items-center gap-1 flex-1">
          ${segments.map((cls) => `<span class="h-2 flex-1 rounded-full ${cls}" data-research-step></span>`).join('')}
        </div>
      </div>
    `;
  }

  function modalContentHtml(type: BuildingType): string {
    const building = buildingOf(type);
    const meta = BUILDING_META[type];
    const targetLevel = building.level + 1;
    const gate = BUILDING_TECH_GATE[type];
    const techLevel = tech?.totalStepsCompleted ?? 0;
    // The HQ gate's two rules both key off the Command Center's own level,
    // read from the same buildings list the rest of this screen renders.
    const hqUnlock = BUILDING_HQ_UNLOCK[type];
    const hqLevel = buildings.find((b) => b.type === 'COMMAND')?.level ?? 0;

    const artTier = buildingArtForLevel(type, building.level);
    const headerIconSrc = artTier?.iconSrc ?? buildingSiteArtFor(type, building.level)?.iconSrc;
    const headerIcon = headerIconSrc
      ? `<img src="${headerIconSrc}" alt="" aria-hidden="true" data-building-modal-icon class="w-9 h-9 shrink-0 object-contain" />`
      : '';
    const header = `
      <div class="flex items-start justify-between gap-3 mb-2 sm:mb-3">
        <div class="flex items-center gap-2 min-w-0">
          ${headerIcon}
          <div class="min-w-0">
            <h2 class="font-heading text-gold text-base truncate">${meta.label}</h2>
            <p class="text-xs text-text-dim">${building.level > 0 ? t('colony.levelN', { level: building.level }) : t('colony.notBuilt')}</p>
          </div>
        </div>
        <button type="button" data-action="close-modal" aria-label="${t('common.close')}"
          class="min-w-11 min-h-11 -m-2 flex items-center justify-center text-text-dim hover:text-text leading-none text-xl shrink-0">&times;</button>
      </div>
      <p class="text-[11px] sm:text-xs text-text-dim leading-snug sm:leading-relaxed mb-2 sm:mb-3">${meta.blurb}</p>
      ${masterControlsHtml(type, building.level)}
    `;

    // One resource per line, label left / amount right — a real column of
    // figures rather than a "·"-joined sentence that wrapped mid-pair and
    // left the numbers unalignable.
    const cost = constructionCost(targetLevel, type);
    const costHtml = (Object.entries(cost) as [ResourceType, number][])
      .map(
        ([res, amt]) =>
          `<span class="flex items-baseline justify-between gap-1.5 sm:gap-3"><span class="text-text-dim inline-flex items-center gap-1">${resourceIcon(res)}${resourceLabel(res)}</span><span class="tabular-nums">${formatAmount(amt)}</span></span>`,
      )
      .join('');

    let action: string;
    if (building.level >= MAX_BUILDING_LEVEL) {
      action = `
        <div class="w-full min-h-11 flex items-center justify-center rounded border border-gold/50 text-gold text-xs">
          ${t('colony.maxLevel', { max: MAX_BUILDING_LEVEL })}
        </div>
      `;
    } else if (building.constructing && building.constructionEndAt) {
      const remaining = new Date(building.constructionEndAt).getTime() - Date.now();
      action = `
        <div class="w-full min-h-11 flex items-center justify-center rounded border border-gold-bright/50 text-gold-bright text-sm tabular-nums"
          data-modal-building-countdown="${type}">${t('colony.buildingCountdown', { time: formatCountdown(remaining) })}</div>
      `;
    } else if (techLevel < gate) {
      action = `
        <div class="w-full min-h-11 flex items-center justify-center rounded border border-border text-text-dim text-xs text-center px-2">
          ${t('colony.requires.tech', { required: gate, have: techLevel })}
        </div>
      `;
    } else if (hqLevel < hqUnlock) {
      action = `
        <div class="w-full min-h-11 flex items-center justify-center rounded border border-border text-text-dim text-xs text-center px-2">
          ${t('colony.requires.building', { building: buildingLabel('COMMAND'), required: hqUnlock, have: hqLevel })}
        </div>
      `;
    } else if (BUILDING_WALL_GATE[type] && (buildings.find((b) => b.type === 'BASTION_WALL')?.level ?? 0) < BUILDING_WALL_GATE[type]!) {
      const wallReq = BUILDING_WALL_GATE[type]!;
      const wallLvl = buildings.find((b) => b.type === 'BASTION_WALL')?.level ?? 0;
      action = `
        <div class="w-full min-h-11 flex items-center justify-center rounded border border-border text-text-dim text-xs text-center px-2">
          ${t('colony.requires.building', { building: buildingLabel('BASTION_WALL'), required: wallReq, have: wallLvl })}
        </div>
      `;
    } else if (type !== 'COMMAND' && building.level >= hqLevel) {
      // The HQ caps every other building at its own level, so the next
      // upgrade is unavailable until the Command Center itself moves first.
      action = `
        <div class="w-full min-h-11 flex items-center justify-center rounded border border-border text-text-dim text-xs text-center px-2">
          ${t('colony.capped', { building: buildingLabel('COMMAND'), level: hqLevel })}
        </div>
      `;
    } else {
      action = `
        <button type="button" data-action="construct-building" data-building-type="${type}"
          class="font-button w-full min-h-11 px-3 bg-gold text-bg text-sm rounded transition-colors duration-[120ms] hover:bg-gold-bright touch-manipulation">
          ${building.level > 0 ? t('colony.upgradeTo', { level: targetLevel }) : t('colony.construct')}
        </button>
      `;
    }

    const trainingSection = type === 'BARRACKS' || type === 'SCOUT_GARAGE' || type === 'SIEGE_WORKSHOP'
      ? `<div class="mt-3 pt-3 border-t border-border">
           <h3 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">${t('colony.trainingHeading')}</h3>
           ${type === 'BARRACKS' && building.level >= 1 ? barracksCrewSummaryHtml() : ''}
           ${building.level >= 1
             ? trainingRowsHtml(type)
             : `<p class="text-xs text-text-dim">${t('colony.constructToTrain')}</p>`}
         </div>`
      : '';

    // The stats block is a real two-column grid, not three justify-between
    // flex rows. The old layout put a free-form sentence from
    // BUILDING_META.effect() in the right-hand cell of row 1, so that cell
    // wrapped to a different width than the Cost and Build-time cells below
    // it and nothing lined up. A grid gives every label one column and every
    // value another, whatever their length.
    const isMaxed = building.level >= MAX_BUILDING_LEVEL;
    return `
      ${header}
      <dl class="bg-surface-2 border border-border rounded-lg p-2.5 sm:p-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 sm:gap-x-4 gap-y-1.5 sm:gap-y-2 text-[11px] sm:text-xs">
        <dt class="text-text-dim">${t('colony.nextLevel')}</dt>
        <dd class="text-text font-heading tabular-nums">${isMaxed ? `<span class="text-gold">${t('colony.alreadyMax')}</span>` : t('colony.levelShort', { level: targetLevel })}</dd>

        <dt class="text-text-dim">${t('colony.effectLabel')}</dt>
        <dd class="text-text flex flex-col gap-0.5">${effectLinesHtml(meta.effect(building.level))}</dd>

        ${isMaxed ? '' : `
        <dt class="text-text-dim">${t('colony.costLabel')}</dt>
        <dd class="text-text flex flex-wrap gap-x-3 gap-y-0.5 sm:flex-col sm:gap-0.5">${costHtml}</dd>

        <dt class="text-text-dim">${t('colony.buildTime')}</dt>
        <dd class="text-text tabular-nums">${formatDuration(constructionDurationMs(targetLevel, type))}</dd>
        `}
      </dl>
      <div class="mt-2 sm:mt-3">${action}</div>
      ${trainingSection}
      ${type === 'COMMAND' ? techBlockHtml() : ''}
    `;
  }

  /** Re-renders the open modal's contents in place. Called after any data change so an open modal never shows stale state. */
  function refreshModal(): void {
    if (!modalEl || !openBuildingType) return;
    const body = modalEl.querySelector('[data-modal-body]');
    if (body) body.innerHTML = modalContentHtml(openBuildingType);
  }

  function openModal(type: BuildingType): void {
    closeModal();
    openBuildingType = type;
    const el = document.createElement('div');
    el.className =
      'fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.innerHTML = `
      <!-- The panel itself never scrolls (it carries the entrance transform, backdrop blur and will-change, which
           break touch scrolling on iOS); the body inside it is the scroller. dvh = the VISIBLE viewport, so the
           bottom is not hidden behind the browser toolbar. -->
      <div data-modal-panel
        class="popup-enter instrument-wash w-full sm:max-w-md max-h-[88dvh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface border border-border border-t-2 border-t-gold rounded-t-xl sm:rounded-xl panel-glow pt-3 px-3 sm:p-4">
        <div data-modal-body class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:pb-0" style="-webkit-overflow-scrolling: touch">${modalContentHtml(type)}</div>
      </div>
    `;
    // Backdrop click closes; clicks inside the panel must not.
    el.addEventListener('click', (e) => {
      if (e.target === el) closeModal();
    });
    root.appendChild(el);
    modalEl = el;
    modalScrollbar = attachGameScrollbar(el.querySelector('[data-modal-body]') as HTMLElement);
  }

  // ------------------------------------------------------------------- data

  async function loadAll(): Promise<void> {
    try {
      const [c, b, r, techResult] = await Promise.all([fetchColony(), fetchBuildings(), fetchRoster(), fetchTech()]);
      if (disposed) return;
      colony = c;
      colonyFetchedAt = Date.now();
      buildings = mergeBuildings(b);
      roster = mergeRoster(r);
      tech = techResult;
      loaded = true;
      render();
      if (!cityView) {
        cityView = mountColonyCityView(
          container,
          () => buildings,
          openModal,
          buildingLabel,
          () => tech?.totalStepsCompleted ?? 0,
        );
        clouds = mountColonyClouds(container);
      }
      // Secondary panels — a failure here must not blank the whole screen.
      void refreshCapacityPanels();
    } catch (err) {
      if (disposed) return;
      container.innerHTML = `<div class="text-center text-error py-10">${escapeHtml((err as Error).message)}</div>`;
    }
  }

  /** Crew accommodation + construction slots + the Delta-V balance. All are read-only summaries, so a failure just leaves the last-known values. */
  async function refreshCapacityPanels(): Promise<void> {
    const [acc, cap, bal] = await Promise.allSettled([
      fetchAccommodation(),
      fetchConstructionCapacity(),
      fetchStoreBalance(),
    ]);
    if (disposed) return;
    let changed = false;
    if (acc.status === 'fulfilled') {
      accommodation = acc.value;
      changed = true;
    }
    if (cap.status === 'fulfilled') {
      capacity = cap.value;
      changed = true;
    }
    if (bal.status === 'fulfilled') {
      deltaV = bal.value.premium;
      changed = true;
    }
    if (changed && loaded) render();
    // Covers the Barracks modal's own crew-space line (barracksCrewSummaryHtml)
    // — render() above only repaints the sidebar's Power panel, not an open
    // modal, which lives outside `container`.
    if (changed) refreshModal();
  }

  async function resyncColony(): Promise<void> {
    if (disposed) return;
    try {
      const c = await fetchColony();
      if (disposed) return;
      colony = c;
      colonyFetchedAt = Date.now();
      patchResources();
    } catch {
      // Keep last-known display; the next resync interval retries.
    }
  }

  async function refetchBuildings(): Promise<void> {
    if (buildingsInFlight || disposed) return;
    buildingsInFlight = true;
    try {
      const result = await fetchBuildings();
      if (disposed) return;
      buildings = mergeBuildings(result);
      render();
      refreshModal();
    } catch {
      // Keep last-known state; the next tick/interval retries.
    } finally {
      buildingsInFlight = false;
      buildingsRetryAfter = Date.now() + REFETCH_COOLDOWN_MS;
    }
  }

  async function refetchRoster(): Promise<void> {
    if (rosterInFlight || disposed) return;
    rosterInFlight = true;
    try {
      const result = await fetchRoster();
      if (disposed) return;
      roster = mergeRoster(result);
      render();
      refreshModal();
    } catch {
      // Keep last-known state; the next tick/interval retries.
    } finally {
      rosterInFlight = false;
      rosterRetryAfter = Date.now() + REFETCH_COOLDOWN_MS;
    }
  }

  async function refetchTech(): Promise<void> {
    if (techInFlight || disposed) return;
    techInFlight = true;
    try {
      const result = await fetchTech();
      if (disposed) return;
      tech = result;
      render();
      refreshModal();
    } catch {
      // Keep last-known state; the next tick/interval retries.
    } finally {
      techInFlight = false;
      techRetryAfter = Date.now() + REFETCH_COOLDOWN_MS;
    }
  }

  function tick(): void {
    if (!loaded || disposed) return;
    patchResources();
    refreshDataPopup();

    const now = Date.now();
    let buildingDone = false;
    for (const building of buildings) {
      if (building.constructing && building.constructionEndAt) {
        const remaining = new Date(building.constructionEndAt).getTime() - now;
        if (remaining <= 0) {
          buildingDone = true;
        } else {
          // The buildings grid was replaced by the canvas + modal, so the
          // only text countdown for a building is the open modal's.
          const el = modalEl?.querySelector(`[data-modal-building-countdown="${building.type}"]`);
          if (el) el.textContent = t('colony.buildingCountdown', { time: formatCountdown(remaining) });
        }
      }
    }
    // Redraw the canvas every tick too, so in-progress construction rings
    // stay in sync without waiting for the next data refetch.
    cityView?.redraw();
    if (buildingDone && !buildingsInFlight && now >= buildingsRetryAfter) void refetchBuildings();

    let rosterDone = false;
    for (const unit of roster) {
      if (unit.training && unit.nextUnitAt) {
        const remaining = new Date(unit.nextUnitAt).getTime() - now;
        if (remaining <= 0) {
          rosterDone = true;
        } else {
          const sidebarEl = container.querySelector(`[data-roster-countdown="${unit.unitType}"]`);
          if (sidebarEl) sidebarEl.textContent = `+${unit.trainQuantity} · ${formatCountdown(remaining)}`;
          const modalRowEl = modalEl?.querySelector(`[data-modal-roster-countdown="${unit.unitType}"]`);
          if (modalRowEl) modalRowEl.textContent = t('colony.trainingCountdown', { quantity: unit.trainQuantity, time: formatCountdown(remaining) });
        }
      }
    }
    if (rosterDone && !rosterInFlight && now >= rosterRetryAfter) void refetchRoster();

    if (tech?.researching && tech.researchEndAt) {
      const remaining = new Date(tech.researchEndAt).getTime() - now;
      if (remaining <= 0) {
        if (!techInFlight && now >= techRetryAfter) void refetchTech();
      } else {
        const el = modalEl?.querySelector('[data-tech-countdown]');
        if (el) el.textContent = formatCountdown(remaining);
      }
    }
  }

  const tickInterval = setInterval(tick, TICK_INTERVAL_MS);
  const resyncInterval = setInterval(() => void resyncColony(), RESYNC_INTERVAL_MS);

  // ---------------------------------------------------------------- actions

  async function handleConstruct(type: BuildingType): Promise<void> {
    try {
      await constructBuilding(type);
      if (disposed) return;
      showToast(t('colony.toast.constructionStarted', { building: buildingLabel(type) }), 'success');
      // The server already debited resources; without this, the sidebar keeps
      // extrapolating from the pre-spend snapshot until the next 30s resync,
      // making the spend look like it never happened.
      await resyncColony();
      await refetchBuildings();
      void refreshCapacityPanels();
    } catch (err) {
      if (disposed) return;
      showToast((err as Error).message, 'error');
    }
  }

  /**
   * MASTER testing control, dev build only (see masterControlsHtml below) —
   * the backend independently 403s outside development/test regardless of
   * how this is reached, so there is nothing for a production build to gate
   * besides not showing the buttons at all.
   */
  async function handleDebugAdjustLevel(type: BuildingType, delta: 1 | -1): Promise<void> {
    try {
      await debugAdjustBuildingLevel(type, delta);
      if (disposed) return;
      await resyncColony();
      await refetchBuildings();
      void refreshCapacityPanels();
    } catch (err) {
      if (disposed) return;
      showToast((err as Error).message, 'error');
    }
  }

  async function handleTrain(unitType: UnitType): Promise<void> {
    const input = (modalEl ?? container).querySelector(
      `[data-quantity-input="${unitType}"]`,
    ) as HTMLInputElement | null;
    const quantity = Math.max(1, Math.floor(Number(input?.value)) || 1);
    try {
      await trainUnit(unitType, quantity);
      if (disposed) return;
      showToast(t('colony.toast.trainingStarted', { unit: unitLabel(unitType) }), 'success');
      await resyncColony();
      await refetchRoster();
      void refreshCapacityPanels();
    } catch (err) {
      if (disposed) return;
      showToast((err as Error).message, 'error');
    }
  }

  async function handleCancelTrain(unitType: UnitType): Promise<void> {
    try {
      await cancelTraining(unitType);
      if (disposed) return;
      showToast(t('colony.toast.trainingCancelled', { unit: unitLabel(unitType) }), 'info');
      await resyncColony();
      await refetchRoster();
      void refreshCapacityPanels();
    } catch (err) {
      if (disposed) return;
      showToast((err as Error).message, 'error');
    }
  }

  async function handleLevelUp(unitType: UnitType): Promise<void> {
    try {
      await levelUpUnit(unitType);
      if (disposed) return;
      showToast(t('colony.toast.leveledUp', { unit: unitLabel(unitType) }), 'success');
      await resyncColony();
      await refetchRoster();
    } catch (err) {
      if (disposed) return;
      showToast((err as Error).message, 'error');
    }
  }

  async function handleResearch(): Promise<void> {
    try {
      await researchTech();
      if (disposed) return;
      showToast(t('colony.toast.researchStarted'), 'success');
      await resyncColony();
      await refetchTech();
    } catch (err) {
      if (disposed) return;
      showToast((err as Error).message, 'error');
    }
  }

  /** One delegated handler on `root` so it covers both the screen and the modal (which lives outside `container`). */
  function onClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const actionEl = target.closest('[data-action]') as HTMLElement | null;
    if (!actionEl) return;
    const action = actionEl.dataset.action;
    if (action === 'construct-building') {
      void handleConstruct(actionEl.dataset.buildingType as BuildingType);
    } else if (action === 'train-unit') {
      void handleTrain(actionEl.dataset.unitType as UnitType);
    } else if (action === 'cancel-train') {
      void handleCancelTrain(actionEl.dataset.unitType as UnitType);
    } else if (action === 'level-up-unit') {
      void handleLevelUp(actionEl.dataset.unitType as UnitType);
    } else if (action === 'research-tech') {
      void handleResearch();
    } else if (action === 'debug-adjust-level') {
      void handleDebugAdjustLevel(actionEl.dataset.buildingType as BuildingType, Number(actionEl.dataset.delta) as 1 | -1);
    } else if (action === 'close-modal') {
      closeModal();
    } else if (action === 'open-data') {
      openDataPopup(actionEl.dataset.dataKind as DataKind);
    } else if (action === 'close-data') {
      closeDataPopup();
    }
  }
  root.addEventListener('click', onClick);

  /** Live-updates a quantity input's own cost line as the player types — the server is still the only one that actually charges anything. */
  function onInput(e: Event): void {
    const target = e.target as HTMLElement;
    const unitType = target.dataset.quantityInput as UnitType | undefined;
    if (!unitType) return;
    const quantity = Math.max(1, Math.floor(Number((target as HTMLInputElement).value)) || 1);
    const costEl = (modalEl ?? container).querySelector(`[data-training-cost="${unitType}"]`);
    if (costEl) costEl.innerHTML = t('colony.cost', { cost: trainingCostHtml(unitType, quantity) });
  }
  root.addEventListener('input', onInput);

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && dataEl) closeDataPopup();
    else if (e.key === 'Escape' && modalEl) closeModal();
  }
  document.addEventListener('keydown', onKeydown);

  void loadAll();

  return () => {
    disposed = true;
    clearInterval(tickInterval);
    clearInterval(resyncInterval);
    root.removeEventListener('click', onClick);
    root.removeEventListener('input', onInput);
    document.removeEventListener('keydown', onKeydown);
    closeModal();
    closeDataPopup();
    cityView?.destroy();
    cityView = null;
    clouds?.destroy();
    clouds = null;
    boostHudTeardown?.();
    boostHudTeardown = null;
  };
}

export const colonyTab: GameTab = {
  id: 'colony',
  label: t('nav.colony'),
  icon: 'colony',
  mount: renderColonyScreen,
};
